/**
 * Dry/live run detail — the §10.1 preview screen. Mirrors what v2.9's
 * writePreview_ put on the "Payroll Preview" tab: payee rows for the Bank &
 * LO Workbook plus the queued pipeline writes — with per-payee totals and
 * the flag investigation queue on top.
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  ButtonGroup,
  Code,
  Flex,
  Heading,
  Input,
  Link,
  Stat,
  StatGroup,
  StatLabel,
  StatNumber,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { payrollApi } from '../api';
import LiveRunModal from './LiveRunModal';

const sheetUrl = (id) => `https://docs.google.com/spreadsheets/d/${id}`;

const money = (v) => (v === null || v === undefined || v === '' ? '—' : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const PRICING_COLORS = { LP: 'green', BP: 'blue', FLAT: 'purple', HELOC_OVERRIDE: 'cyan', HOUSE: 'gray', SKIP: 'red' };
const PAYEE_TYPE_COLORS = { Primary: 'green', Secondary: 'blue', '3rd party': 'purple', 'True-up': 'orange' };

function PayeeTotalsTable({ totals, payeeRows }) {
  const routedByEmail = useMemo(() => {
    const m = {};
    (payeeRows || []).forEach((p) => { if (!(p.payee_email in m)) m[p.payee_email] = p.routed_to; });
    return m;
  }, [payeeRows]);

  const entries = Object.entries(totals || {}).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return null;
  return (
    <Box overflowX="auto" borderWidth="1px" borderRadius="md" mb={4} maxW="640px">
      <Table size="sm">
        <Thead><Tr><Th>Payee</Th><Th>Routed to</Th><Th isNumeric>Total</Th></Tr></Thead>
        <Tbody>
          {entries.map(([email, total]) => (
            <Tr key={email}>
              <Td>{email}</Td>
              <Td>
                <Badge colorScheme={routedByEmail[email] === 'w2_comp_tab' ? 'green' : 'purple'}>
                  {routedByEmail[email] === 'w2_comp_tab' ? 'W2 comp tab' : 'Outside 1099'}
                </Badge>
              </Td>
              <Td isNumeric fontWeight="semibold">{money(total)}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

export default function RunDetail({ detail, isSuperAdmin = false, onRunLaunched = null }) {
  const { run, runRows, payeeRows } = detail;
  const expandedBg = useColorModeValue('gray.50', 'gray.700');
  const toast = useToast();
  const liveModal = useDisclosure();

  const [view, setView] = useState('payees');
  const [rowFilter, setRowFilter] = useState('ALL');
  const [expandedId, setExpandedId] = useState(null);
  const [resumeText, setResumeText] = useState('');
  const [resuming, setResuming] = useState(false);

  const totals = run.totals || {};
  const isDry = run.run_type === 'dry';
  // totals.rehearsal lands on completion; sheets_write_state.is_rehearsal is
  // written up-front, so failed rehearsals are labeled too.
  const isRehearsal = !!(totals.rehearsal || (run.sheets_write_state && run.sheets_write_state.is_rehearsal));
  const canLaunchLive = isDry && run.status === 'completed' && isSuperAdmin && !!onRunLaunched;
  const canResume = run.run_type === 'live' && run.status === 'failed' && isSuperAdmin && !!onRunLaunched;

  const resume = async () => {
    setResuming(true);
    try {
      const res = await payrollApi.liveRun({ resumeRunId: run.id, confirmText: resumeText });
      toast({ title: 'Run resumed and completed', status: 'success', duration: 8000, isClosable: true });
      onRunLaunched(res.runId || run.id);
    } catch (err) {
      toast({ title: 'Resume failed', description: err.message, status: 'error', duration: null, isClosable: true });
    } finally {
      setResuming(false);
    }
  };

  const flaggedRows = useMemo(() => (runRows || []).filter((r) => (r.flags || []).length > 0), [runRows]);
  const filteredRows = useMemo(() => {
    const rows = runRows || [];
    switch (rowFilter) {
      case 'FLAGGED': return flaggedRows;
      case 'COMPUTED': return rows.filter((r) => r.computed_primary !== null && r.computed_primary !== undefined);
      case 'MANUAL': return rows.filter((r) => r.manual_prefilled);
      case 'SKIPPED': return rows.filter((r) => r.pricing === 'SKIP');
      default: return rows;
    }
  }, [runRows, rowFilter, flaggedRows]);

  const trueupCount = useMemo(() => (payeeRows || []).filter((p) => p.payee_type === 'True-up').length, [payeeRows]);

  return (
    <Box mt={6}>
      <Flex align="baseline" justify="space-between" wrap="wrap" gap={2}>
        <Heading size="md">
          {isDry ? 'Dry run' : 'Live run'} — {new Date(run.started_at).toLocaleString('en-US', { timeZone: 'America/New_York' })} ET
        </Heading>
        <Text fontSize="xs" color="gray.500">
          engine <Code fontSize="xs">{run.engine_version}</Code> · snapshot <Code fontSize="xs">{(run.snapshot_hash || '').slice(0, 12)}…</Code>
        </Text>
      </Flex>

      <StatGroup my={4} flexWrap="wrap" gap={4}>
        <Stat><StatLabel>Rows processed</StatLabel><StatNumber>{totals.processed ?? run.rows_computed ?? '—'}</StatNumber></Stat>
        <Stat><StatLabel>Computed</StatLabel><StatNumber>{run.rows_computed ?? '—'}</StatNumber></Stat>
        <Stat><StatLabel>Skipped</StatLabel><StatNumber>{run.rows_skipped ?? '—'}</StatNumber></Stat>
        <Stat><StatLabel>Payee rows</StatLabel><StatNumber>{run.payee_count ?? '—'}</StatNumber></Stat>
        <Stat><StatLabel>True-ups</StatLabel><StatNumber>{trueupCount}</StatNumber></Stat>
        <Stat><StatLabel>Needs review</StatLabel><StatNumber>{flaggedRows.length}</StatNumber></Stat>
      </StatGroup>

      {isDry && (
        <Alert status="info" borderRadius="md" mb={3} fontSize="sm">
          <AlertIcon />
          <Box flex="1">
            Preview only — nothing was written to the pipeline or the Bank &amp; LO Workbook. Review the
            flagged rows and payee totals{canLaunchLive ? ', then launch the live run from here' : ''}.
          </Box>
          {canLaunchLive && (
            <Button size="sm" colorScheme="red" ml={3} flexShrink={0} onClick={liveModal.onOpen}>
              Start live run…
            </Button>
          )}
        </Alert>
      )}

      {run.run_type === 'live' && (
        <Alert status={run.status === 'completed' ? 'success' : run.status === 'failed' ? 'error' : 'info'} borderRadius="md" mb={3} fontSize="sm">
          <AlertIcon />
          <Box flex="1">
            {isRehearsal && <Badge colorScheme="blue" mr={2}>REHEARSAL (spreadsheet copies)</Badge>}
            Batch <Code fontSize="xs">{run.batch_label}</Code>
            {totals.pipelineWritten != null && ` — ${totals.pipelineWritten} pipeline rows written`}
            {totals.outside1099 && totals.outside1099.count ? `, ${totals.outside1099.count} × 1099 pasted (row ${totals.outside1099.pasteRow})` : ''}
            {totals.w2 && totals.w2.count ? `, ${totals.w2.count} × W2 → "${totals.w2.tab}" (row ${totals.w2.pasteRow})` : ''}
            {isRehearsal && (run.workbook_spreadsheet_id || run.pipeline_spreadsheet_id) && (
              <Flex gap={4} mt={1} wrap="wrap">
                {run.workbook_spreadsheet_id && (
                  <Link href={sheetUrl(run.workbook_spreadsheet_id)} isExternal fontWeight="semibold" fontSize="sm">
                    Open workbook copy <ExternalLinkIcon mx="2px" />
                  </Link>
                )}
                {run.pipeline_spreadsheet_id && (
                  <Link href={sheetUrl(run.pipeline_spreadsheet_id)} isExternal fontWeight="semibold" fontSize="sm">
                    Open pipeline copy <ExternalLinkIcon mx="2px" />
                  </Link>
                )}
              </Flex>
            )}
            {run.status === 'failed' && run.error && (
              <Text mt={1} fontSize="xs" color="red.400">{run.error}</Text>
            )}
          </Box>
        </Alert>
      )}

      {canResume && (
        <Flex align="center" gap={2} mb={3} p={3} borderWidth="1px" borderRadius="md" borderColor="red.300" wrap="wrap">
          <Text fontSize="sm" flex="1" minW="260px">
            This live run failed part-way. Resuming replays only the unwritten pipeline rows and
            pending workbook payees (already-stamped rows are recognized and skipped). Type the batch
            label <Code fontSize="xs">{run.batch_label}</Code> to confirm.
          </Text>
          <Input size="sm" maxW="160px" fontFamily="mono" placeholder="MM/dd HH:mm"
            value={resumeText} onChange={(e) => setResumeText(e.target.value)} />
          <Button size="sm" colorScheme="red" onClick={resume} isLoading={resuming}
            isDisabled={resumeText !== run.batch_label}>
            Resume run
          </Button>
        </Flex>
      )}

      {canLaunchLive && (
        <LiveRunModal
          isOpen={liveModal.isOpen}
          onClose={liveModal.onClose}
          previewRun={run}
          onLaunched={(runId, wasRehearsal) => {
            toast({
              title: wasRehearsal ? 'Rehearsal run completed' : 'LIVE run completed',
              status: 'success', duration: 8000, isClosable: true,
            });
            onRunLaunched(runId);
          }}
        />
      )}

      <ButtonGroup size="xs" isAttached variant="outline" mb={3}>
        <Button onClick={() => setView('payees')} isActive={view === 'payees'}>
          Payee rows ({(payeeRows || []).length})
        </Button>
        <Button onClick={() => setView('rows')} isActive={view === 'rows'}>
          Pipeline rows ({(runRows || []).length})
        </Button>
      </ButtonGroup>

      {view === 'payees' && (
        <>
          <PayeeTotalsTable totals={totals.payeeTotals} payeeRows={payeeRows} />
          <Box overflowX="auto" borderWidth="1px" borderRadius="md">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Client</Th>
                  <Th isNumeric>Amount</Th>
                  <Th>Payee</Th>
                  <Th>Type</Th>
                  <Th>Routed</Th>
                  <Th>Flags</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(payeeRows || []).length === 0 && (
                  <Tr><Td colSpan={6}><Text py={3} textAlign="center" color="gray.500">No payee rows.</Text></Td></Tr>
                )}
                {(payeeRows || []).map((p) => (
                  <React.Fragment key={p.id}>
                    <Tr cursor="pointer" onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}>
                      <Td maxW="220px" isTruncated title={p.client}>{p.client}</Td>
                      <Td isNumeric fontWeight="semibold">{money(p.amount)}</Td>
                      <Td maxW="200px" isTruncated title={p.payee_email}>{p.payee_email}</Td>
                      <Td><Badge colorScheme={PAYEE_TYPE_COLORS[p.payee_type] || 'gray'}>{p.payee_type}</Badge></Td>
                      <Td>
                        <Badge variant="outline" colorScheme={p.routed_to === 'w2_comp_tab' ? 'green' : 'purple'}>
                          {p.routed_to === 'w2_comp_tab' ? 'W2' : '1099'}
                        </Badge>
                      </Td>
                      <Td>
                        <Flex gap={1} wrap="wrap" maxW="280px">
                          {(p.flags || []).map((fl) => <Tag key={fl} size="sm" colorScheme="orange">{fl}</Tag>)}
                        </Flex>
                      </Td>
                    </Tr>
                    {expandedId === p.id && (
                      <Tr bg={expandedBg}>
                        <Td colSpan={6}>
                          <Text fontSize="sm" py={1}><b>Comp code:</b> <Code fontSize="xs">{p.comp_code || '—'}</Code></Text>
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                ))}
              </Tbody>
            </Table>
          </Box>
        </>
      )}

      {view === 'rows' && (
        <>
          <ButtonGroup size="xs" isAttached variant="outline" mb={3}>
            {[['ALL', `All (${(runRows || []).length})`],
              ['FLAGGED', `Flagged (${flaggedRows.length})`],
              ['COMPUTED', 'Computed'],
              ['MANUAL', 'Manual'],
              ['SKIPPED', 'Skipped']].map(([key, label]) => (
                <Button key={key} onClick={() => setRowFilter(key)} isActive={rowFilter === key}>{label}</Button>
            ))}
          </ButtonGroup>

          <Box overflowX="auto" borderWidth="1px" borderRadius="md">
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th isNumeric>Row</Th>
                  <Th>Client</Th>
                  <Th>Primary LO</Th>
                  <Th>Month</Th>
                  <Th>Pricing</Th>
                  <Th isNumeric>Primary</Th>
                  <Th isNumeric>Secondary</Th>
                  <Th>Flags</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredRows.length === 0 && (
                  <Tr><Td colSpan={8}><Text py={3} textAlign="center" color="gray.500">No rows for this filter.</Text></Td></Tr>
                )}
                {filteredRows.map((r) => (
                  <React.Fragment key={r.id}>
                    <Tr cursor="pointer" onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
                      <Td isNumeric>{r.pipeline_row_number}</Td>
                      <Td maxW="180px" isTruncated title={r.client}>{r.client}</Td>
                      <Td maxW="180px" isTruncated title={r.primary_email}>{r.primary_email}</Td>
                      <Td>{r.month}</Td>
                      <Td>
                        {r.manual_prefilled
                          ? <Badge colorScheme="yellow">MANUAL</Badge>
                          : <Badge colorScheme={PRICING_COLORS[r.pricing] || 'gray'}>{r.pricing || '—'}</Badge>}
                        {r.tier_applied ? <Badge ml={1} variant="outline">T{r.tier_applied}</Badge> : null}
                      </Td>
                      <Td isNumeric>{money(r.computed_primary)}</Td>
                      <Td isNumeric>{money(r.computed_secondary)}</Td>
                      <Td>
                        <Flex gap={1} wrap="wrap" maxW="300px">
                          {(r.flags || []).map((fl) => <Tag key={fl} size="sm" colorScheme="orange">{fl}</Tag>)}
                        </Flex>
                      </Td>
                    </Tr>
                    {expandedId === r.id && (
                      <Tr bg={expandedBg}>
                        <Td colSpan={8}>
                          <Box fontSize="sm" py={1}>
                            <Text><b>Formula:</b> <Code fontSize="xs">{r.formula || '—'}</Code></Text>
                            {r.comp_code_append && (
                              <Text mt={1}><b>Comp-code append:</b> <Code fontSize="xs">{r.comp_code_append}</Code></Text>
                            )}
                            {r.skip_reason && (
                              <Text mt={1}><b>Skip reason:</b> <Code fontSize="xs">{r.skip_reason}</Code></Text>
                            )}
                            <Text mt={1}>
                              <b>Deal source:</b> {r.deal_source || '—'}{r.deal_source_how ? ` (${r.deal_source_how})` : ''}
                              {' · '}<b>Margin:</b> {r.margin_lender || '—'}{r.margin_column ? ` / ${r.margin_column}` : ''}
                              {r.margin_pct != null ? ` @ ${r.margin_pct}%` : ''}
                              {' · '}<b>Rate set:</b> {r.rate_set || '—'}
                            </Text>
                          </Box>
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                ))}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
}
